/**
 * ATLAS Field Conditional Rules Engine (DEXPDF_10 v F.1.10)
 *
 * Client-side implementation of all "Bedingtes Datenfeld" (D-status) rules
 * from the official ATLAS E_EXP_DAT specification.
 *
 * 5 rule categories:
 * 1. exportDeclarationType (8-digit pattern matching)
 * 2. partyConstellation (4-digit party group control)
 * 3. identificationNumber toggle (name/address vs EORI)
 * 4. Codelist-driven sub-field status (I0922/I0932/I0912)
 * 5. Procedure-driven (requestedProcedure + previousProcedure)
 */

export type FieldStatus = 'required' | 'optional' | 'hidden' | 'locked';

// Utility: get nested value from object using dot notation
function getNestedValue(data: any, path: string, defaultValue: any = null): any {
  const keys = path.split('.');
  let current = data;
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return defaultValue;
    }
  }
  return current;
}

// Pattern matching for 8-digit export declaration type
// Uses bullet character as wildcard
function matchesPattern(value: string, pattern: string): boolean {
  if (!value || !pattern || pattern.length !== 8) return false;
  const padded = value.padEnd(8, '0').substring(0, 8);
  for (let i = 0; i < 8; i++) {
    if (pattern[i] === '\u2022') continue; // bullet = wildcard
    if (padded[i] !== pattern[i]) return false;
  }
  return true;
}

function matchesAnyPattern(value: string, patterns: string[]): boolean {
  return patterns.some(p => matchesPattern(value, p));
}

// ============================================================================
// RULE DEFINITIONS
// ============================================================================

interface FieldRule {
  requiredWhen?: string[];
  hiddenWhen?: string[];
  description: string;
}

const EXPORT_DECL_TYPE_RULES: Record<string, FieldRule> = {
  // Dates
  'kopf.massgeblichesDatum': {
    requiredWhen: ['1\u2022\u2022\u2022\u2022\u2022\u2022\u2022', '20000000'],
    description: 'Decisive date required for retroactive/collective declarations',
  },
  'kopf.kopfDatumdesAusgangs': {
    requiredWhen: ['1\u2022\u2022\u2022\u2022\u2022\u2022\u2022'],
    description: 'Exit date required for retroactive declarations',
  },
  'kopf.zeitpunktDerGestellung': {
    requiredWhen: ['\u2022\u2022\u2022\u2022\u20222\u2022\u2022'],
    description: 'Presentation time required for AWV presentation',
  },
  'kopf.zeitpunktdesEndesderLadetatigkeit': {
    requiredWhen: ['\u2022\u2022\u2022\u2022\u20222\u2022\u2022'],
    description: 'Loading end time required for AWV presentation',
  },
  // Customs offices
  'kopf.gestellungszollstelle': {
    requiredWhen: ['\u2022\u2022\u2022\u2022\u20224\u2022\u2022'],
    description: 'Presentation customs office required for CCL',
  },
  'kopf.ausfuhrZollstelleFurDieErganzende': {
    requiredWhen: ['\u2022\u2022\u2022\u2022\u2022\u20221\u2022'],
    description: 'Supplement export office required for incomplete declarations',
  },
  'kopf.vorgeseheneAusgangszollstelleDienststellennummer': {
    requiredWhen: ['0\u2022\u2022\u2022\u2022\u2022\u2022\u2022'],
    description: 'Planned exit office required for advance declarations',
  },
  'kopf.tatsachlicheAusgangszollstelleDienststellennummer': {
    requiredWhen: ['1\u2022\u2022\u2022\u2022\u2022\u2022\u2022'],
    description: 'Actual exit office required for retroactive declarations',
  },
  // Shipment level
  'lieferung.artdesGeschafts': {
    requiredWhen: ['\u2022\u2022\u2022\u2022\u20220\u2022\u2022'],
    hiddenWhen: [
      '\u2022\u2022\u2022\u2022\u20221\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20222\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20223\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20224\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20229\u2022\u2022',
    ],
    description: 'Nature of transaction required for standard procedure',
  },
  'lieferung.lieferbedingungIncotermCode': {
    requiredWhen: ['\u2022\u2022\u2022\u2022\u20220\u2022\u2022'],
    description: 'Incoterm required for standard procedure',
  },
  // Consignment level
  'sendung.containerIndikator': {
    requiredWhen: [
      '\u2022\u2022\u2022\u2022\u20220\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20221\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20223\u2022\u2022',
    ],
    description: 'Container indicator required for standard/SDE/exit office procedures',
  },
  'sendung.inlandischerVerkehrszweig': {
    requiredWhen: [
      '\u2022\u2022\u2022\u2022\u20220\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20221\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20223\u2022\u2022',
    ],
    description: 'Inland transport mode required for standard/SDE/exit office procedures',
  },
  'sendung.verkehrszweigAnDerGrenze': {
    requiredWhen: [
      '\u2022\u2022\u2022\u2022\u20220\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20221\u2022\u2022',
      '\u2022\u2022\u2022\u2022\u20223\u2022\u2022',
    ],
    description: 'Border transport mode required for standard/SDE/exit office procedures',
  },
  'sendung.gesamtgewicht': {
    requiredWhen: ['\u2022\u2022\u2022\u2022\u20220\u2022\u2022'],
    description: 'Gross mass required for standard procedure',
  },
  // Goods item level
  'position.statistischerWert': {
    requiredWhen: ['\u2022\u2022\u2022\u2022\u20220\u2022\u2022'],
    description: 'Statistical value required for standard procedure',
  },
};

// Party constellation rules
interface PartyRule {
  position: number;
  value: string;
  description: string;
}

const PARTY_CONSTELLATION_RULES: Record<string, PartyRule> = {
  vertragspartner: { position: 0, value: '1', description: 'Contractual Partner' },
  ausfuhrer_indirect: { position: 1, value: '1', description: 'Exporter (indirect)' },
  vertreter: { position: 2, value: '1', description: 'Representative' },
  subunternehmer: { position: 3, value: '1', description: 'Subcontractor' },
};

// Procedure-driven rules
const PROCEDURE_RULES: Record<string, { exportDeclTypePatterns?: string[]; previousProcedure?: string[] }> = {
  passiveVeredelung: {
    exportDeclTypePatterns: ['\u2022\u20221\u2022\u2022\u2022\u2022\u2022'],
  },
  verfahrensubergang: { previousProcedure: ['51', '71'] },
  zolllager: { previousProcedure: ['71'] },
  aktiveVeredelung: { previousProcedure: ['51'] },
};

// Countries where postcode is NOT required (codelist C0505 subset)
const C0505_COUNTRIES = new Set([
  'AE', 'AG', 'AI', 'AN', 'AO', 'AW', 'BF', 'BI', 'BJ', 'BM',
  'BO', 'BS', 'BW', 'BZ', 'CD', 'CF', 'CG', 'CI', 'CK', 'CM',
  'CO', 'CW', 'DJ', 'DM', 'DO', 'ER', 'FJ', 'GA', 'GD', 'GH',
  'GM', 'GN', 'GP', 'GQ', 'GT', 'GW', 'GY', 'HK', 'HN', 'IE',
  'JM', 'KI', 'KM', 'KN', 'KP', 'LC', 'LR', 'LS', 'LY', 'ML',
  'MM', 'MO', 'MR', 'MS', 'MU', 'MW', 'NA', 'NR', 'NU', 'PA',
  'QA', 'RW', 'SB', 'SC', 'SL', 'SN', 'SO', 'SR', 'SS', 'ST',
  'SX', 'SY', 'TD', 'TG', 'TK', 'TL', 'TO', 'TT', 'TV', 'UG',
  'VU', 'WS', 'YE', 'ZW',
]);

// ============================================================================
// RULES ENGINE
// ============================================================================

export function evaluateFieldRules(formData: any): Record<string, FieldStatus> {
  const result: Record<string, FieldStatus> = {};

  // Extract driver fields
  const exportDeclType = String(
    getNestedValue(formData, 'kopf.artderAusfuhranmeldung', '') || ''
  ).padEnd(8, '0').substring(0, 8);

  const partyConstellation = String(
    getNestedValue(formData, 'kopf.beteiligtenKonstellation', '0000') || '0000'
  ).padEnd(4, '0').substring(0, 4);

  const totalAmount = getNestedValue(formData, 'kopf.inRechnunggestellterGesamtbetrag');
  const invoiceCurrency = getNestedValue(formData, 'kopf.rechnungswahrung');

  // --- Category 1: exportDeclarationType-driven ---
  for (const [fieldPath, rule] of Object.entries(EXPORT_DECL_TYPE_RULES)) {
    if (rule.requiredWhen && matchesAnyPattern(exportDeclType, rule.requiredWhen)) {
      result[fieldPath] = 'required';
    } else if (rule.hiddenWhen && matchesAnyPattern(exportDeclType, rule.hiddenWhen)) {
      result[fieldPath] = 'hidden';
    } else {
      result[fieldPath] = 'optional';
    }
  }

  // --- Invoice currency / total amount mutual dependency ---
  result['kopf.rechnungswahrung'] = totalAmount && String(totalAmount).trim() ? 'required' : 'optional';
  result['kopf.inRechnunggestellterGesamtbetrag'] = invoiceCurrency && String(invoiceCurrency).trim() ? 'required' : 'optional';

  // --- Category 2: partyConstellation-driven ---
  for (const [groupName, rule] of Object.entries(PARTY_CONSTELLATION_RULES)) {
    const pos = rule.position;
    if (partyConstellation.length > pos && partyConstellation[pos] === rule.value) {
      result[`group.${groupName}`] = 'required';
    } else {
      result[`group.${groupName}`] = 'hidden';
    }
  }

  // --- Category 3: identificationNumber toggle ---
  const partySections = [
    'kopf.ausfuhrer', 'kopf.empfanger', 'kopf.anmelder',
    'kopf.vertreter', 'kopf.subunternehmer', 'kopf.vertragspartner',
  ];

  for (const section of partySections) {
    const idNumber = getNestedValue(formData, `${section}.tin`)
      || getNestedValue(formData, `${section}.identifikationsnummer`);
    const hasId = Boolean(idNumber && String(idNumber).trim());

    if (hasId) {
      result[`${section}.name`] = 'optional';
      result[`${section}.strasse`] = 'optional';
      result[`${section}.plz`] = 'optional';
      result[`${section}.ort`] = 'optional';
      result[`${section}.land`] = 'optional';
      result[`${section}.niederlassungsNummer`] = 'required';
    } else {
      result[`${section}.name`] = 'required';
      result[`${section}.strasse`] = 'required';
      result[`${section}.ort`] = 'required';
      result[`${section}.land`] = 'required';
      result[`${section}.niederlassungsNummer`] = 'optional';

      const country = String(getNestedValue(formData, `${section}.land`, '') || '').toUpperCase();
      result[`${section}.plz`] = country && !C0505_COUNTRIES.has(country) ? 'required' : 'optional';
    }
  }

  // --- Category 5: Procedure-driven ---
  const positions = getNestedValue(formData, 'position', []);
  if (Array.isArray(positions)) {
    for (let idx = 0; idx < positions.length; idx++) {
      const prevProc = String(getNestedValue(positions[idx], 'vorhergehendesVerfahren', '') || '');

      // Outward Processing
      const opRule = PROCEDURE_RULES.passiveVeredelung;
      result[`position[${idx}].passiveVeredelung`] =
        opRule.exportDeclTypePatterns && matchesAnyPattern(exportDeclType, opRule.exportDeclTypePatterns)
          ? 'required' : 'hidden';

      // Procedure Transference
      result[`position[${idx}].verfahrensubergang`] =
        PROCEDURE_RULES.verfahrensubergang.previousProcedure?.includes(prevProc) ? 'required' : 'hidden';

      // Customs Warehousing
      result[`position[${idx}].zolllager`] =
        PROCEDURE_RULES.zolllager.previousProcedure?.includes(prevProc) ? 'required' : 'hidden';

      // Inward Processing
      result[`position[${idx}].aktiveVeredelung`] =
        PROCEDURE_RULES.aktiveVeredelung.previousProcedure?.includes(prevProc) ? 'required' : 'hidden';

      // Transport Charges at item level: mutually exclusive with header level
      const headerTransportCharges = getNestedValue(formData, 'sendung.beforderungskosten');
      const hasHeaderTransportCharges = headerTransportCharges !== null && headerTransportCharges !== undefined && String(headerTransportCharges).trim() !== '';
      result[`position[${idx}].beforderungskosten`] = hasHeaderTransportCharges ? 'hidden' : 'optional';
    }
  }

  // --- Transport Equipment depends on Container Indicator ---
  const containerIndicator = getNestedValue(formData, 'sendung.containerIndikator');
  result['sendung.transportausrustung'] = String(containerIndicator) === '1' ? 'required' : 'optional';

  return result;
}

/**
 * Get the status of a single field given current form data.
 */
export function getFieldStatus(fieldPath: string, formData: any): FieldStatus {
  const allStatuses = evaluateFieldRules(formData);
  return allStatuses[fieldPath] || 'optional';
}

/**
 * Check if a specific field is required given current form data.
 */
export function isFieldRequired(fieldPath: string, formData: any): boolean {
  return getFieldStatus(fieldPath, formData) === 'required';
}

/**
 * Check if a specific field/group should be hidden given current form data.
 */
export function isFieldHidden(fieldPath: string, formData: any): boolean {
  return getFieldStatus(fieldPath, formData) === 'hidden';
}

export default {
  evaluateFieldRules,
  getFieldStatus,
  isFieldRequired,
  isFieldHidden,
};
